import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { usePromptArticles, TPromptArticlesQuery } from '../hooks/usePromptArticles';

type TPreviewState = {
  articleId: string | null;
};

type TPreview = {
  state: TPreviewState;
  setState: (state: TPreviewState) => void;
};

type TPromptPageContextType = {
  promptId: string;
  territoryId: string;
  prompt: TPromptArticlesQuery | null;
  preview: TPreview;
};

const PromptPageContext = createContext<TPromptPageContextType>({
  promptId: '',
  territoryId: '',
  prompt: null,
  preview: {
    state: {
      articleId: null,
    },
    setState: () => {},
  },
});

const usePreviewState = (): TPreview => {
  const router = useRouter();

  const articleId = ((router?.query.preview || null) as string) || null;
  const state = useMemo(() => ({ articleId }), [articleId]);

  const setState = ({ articleId }: TPreviewState) => {
    if (!articleId) {
      const newQuery = { ...(router?.query || {}) };
      delete newQuery.preview;

      router?.replace(
        {
          query: newQuery,
        },
        undefined,
        { shallow: true },
      );
    }

    router?.replace(
      {
        query: { ...router.query, preview: articleId },
      },
      undefined,
      { shallow: true },
    );
  };

  return { state, setState };
};

export const PromptPageContextProvider = ({
  children,
  promptId,
  territoryId,
}: { children: ReactNode } & Omit<TPromptPageContextType, 'preview' | 'prompt'>) => {
  const preview = usePreviewState();
  const prompt = usePromptArticles({ promptId });

  const value = useMemo(() => ({ promptId, territoryId, preview, prompt }), [promptId, territoryId, preview, prompt]);

  return <PromptPageContext.Provider value={value}>{children}</PromptPageContext.Provider>;
};

export const usePromptPageContext = () => useContext(PromptPageContext);
